import useResizeObserver from '@react-hook/resize-observer'
import {
  createContext,
  createRef,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

interface GestureContainerContextData {
  currHeight: RefObject<number>
  currWidth: RefObject<number>
  containerRef: RefObject<HTMLDivElement | null>
  width: number
  updateWidth: (width: number) => void
}
const GestureContainerContext = createContext<GestureContainerContextData>({
  currHeight: createRef<number>() as RefObject<number>,
  currWidth: createRef<number>() as RefObject<number>,
  containerRef: createRef<HTMLDivElement>(),
  width: 0,
  updateWidth: () => {},
})

export const GestureContainerProvider = ({
  children,
  zoom = 1,
}: PropsWithChildren<{ zoom?: number }>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const currWidth = useRef<number>(0)
  const currHeight = useRef<number>(0)
  const [width, setWidth] = useState(0)
  const isInitialized = useRef(false)

  const handleResize = useCallback((entry: ResizeObserverEntry) => {
    currHeight.current = entry.contentRect.height
    currWidth.current = entry.contentRect.width
    if (!isInitialized.current) {
      setWidth(entry.contentRect.width)
      isInitialized.current = true
    }
  }, [])
  useResizeObserver(containerRef, handleResize)
  const value = useMemo<GestureContainerContextData>(
    () => ({ currHeight, currWidth, containerRef, width, updateWidth: w => setWidth(w), zoom }),
    [width, zoom],
  )
  return (
    <GestureContainerContext.Provider value={value}>{children}</GestureContainerContext.Provider>
  )
}

export const useGestureContainer = () => useContext(GestureContainerContext)
